import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Stack,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Button,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  GroupsOutlined as GroupsIcon,
  GroupOutlined as GroupIcon,
  AddOutlined as AddIcon,
  RemoveOutlined as RemoveIcon
} from '@mui/icons-material';
import { listGroups } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useSessionData } from '../../../contexts/SessionDataContext';
import CustomPermissionsDialog from './CustomPermissionsDialog';
import { getUserInitials } from '../../../utils/userUtils';

const PermissionSettings = ({
  permissions,
  onPermissionsChange,
  userProfile,
  isEditing = false,
  entityType = 'item', // 'prompt', 'context', 'collection'
  showCommunityOption = false, // Only for prompts
  onCommunityToggle,
  isCommunityPrompt = false,
  permissionLevels,
  accessList = [],
  onAccessListChange,
  creatorId = null, // ID of the entity creator (prompt/context/collection creator)
  entityData = null, // The full entity object (prompt/context/collection) to check permissions
  visibility = null, // From BuildTab toggle: 'private' | 'team' | 'organization' | 'community' | 'custom'
  openAddAccessRef = null, // Ref to expose openAddAccessDialog for external triggers (e.g. Manage button)
  hideLegacyToggles = false, // When true (prompt builder): hide standard toggles and custom permissions UI
  /** When true (prompts): parent owns non-custom permissions; only push updates when custom accessList changes */
  controlledPermissionsSync = false
}) => {
  const { user } = useAuth();
  const { getGroups } = useSessionData();
  const [userGroups, setUserGroups] = useState([]);
  
  // When editing, we have entity data and current user; useful for debugging but avoid noisy logs in production
  useEffect(() => {
    if (isEditing && entityData && user) {
      // Debug hook point for permission issues
    }
  }, [isEditing, entityData, creatorId, user]);
  
  // Fetch user groups on mount (session cache; needed for checking group permissions)
  useEffect(() => {
    if (!user?.companyId || !user?.id || !isEditing) return;
    let cancelled = false;
    (async () => {
      try {
        const groups = await getGroups({ force: false });
        if (!cancelled) setUserGroups(Array.isArray(groups) ? groups : []);
      } catch (error) {
        console.error('Error fetching user groups:', error);
        if (!cancelled) setUserGroups([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isEditing, getGroups]);
  
  // Check if user can edit permissions (only creator or users with "owner" permission)
  // Users with only "edit" permission cannot change permissions
  const canEditPermissions = React.useMemo(() => {
    // If not editing (new item), always allow
    if (!isEditing) {
      return true;
    }
    
    // If no user when editing, default to false (backend will enforce)
    if (!user) {
      return false;
    }
    
    // If no entity data when editing, default to false (we need entity data to check permissions)
    if (!entityData) {
      return false;
    }
    
    // Creator can always edit permissions
    // Check entityData.userId first (most reliable)
    if (entityData.userId && entityData.userId === user.id) {
      return true;
    }
    
    // Fallback to creatorId prop if entityData.userId is not available
    // But only if creatorId is explicitly provided and doesn't default to current user
    if (!entityData.userId && creatorId && creatorId !== user.id) {
      // creatorId is different from current user, so it's the actual creator
      // But current user is not the creator, so return false
      // Continue to permission check below
    } else if (!entityData.userId && creatorId && creatorId === user.id) {
      // This is a fallback case - if userId is missing but creatorId matches, assume creator
      console.warn('[PermissionSettings] entityData.userId is missing; using creatorId fallback.');
      return true;
    }
    
    // Check if user has "owner" permission (in canDelete array)
    const entityPermissions = entityData.permissions || {};
    const canDelete = entityPermissions.canDelete || [];
    
    // Check direct user ID in canDelete (owner permission)
    if (canDelete.includes(user.id)) {
      return true;
    }
    
    // Check user's team in canDelete (owner permission)
    if (user.teamId && canDelete.includes(user.teamId)) {
      return true;
    }
    
    // Check if user belongs to any groups with owner permission
    const userGroupIds = userGroups.map(g => g.id);
    if (userGroupIds.length > 0) {
      if (userGroupIds.some(groupId => canDelete.includes(groupId))) {
        return true;
      }
    }
    
    // If we reach here, user is NOT the creator and NOT in canDelete (owner permission)
    // Therefore, they cannot edit permissions (whether they have edit permission or not)
    return false;
  }, [isEditing, user, entityData, creatorId, userGroups]);
  
  // Permission state management - default to sharing with company and team (not private)
  const [isPrivate, setIsPrivate] = useState(false);
  const [shareWithCompany, setShareWithCompany] = useState(true);
  const [shareWithTeam, setShareWithTeam] = useState(true);
  const [useCustomPermissions, setUseCustomPermissions] = useState(false);
  
  // Group selection state
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingGroups, setLoadingGroups] = useState(false);
  
  // Custom permissions dialog
  const [showAddAccessDialog, setShowAddAccessDialog] = useState(false);
  
  // Menu state for editing permission levels
  const [permissionMenuAnchor, setPermissionMenuAnchor] = useState(null);
  const [editingAccessId, setEditingAccessId] = useState(null);
  
  // Ref to track if we're initializing to avoid loops
  const isInitializing = useRef(false);
  const hasInitialized = useRef(false);
  const lastPermissionsRef = useRef(null);
  const isUserEditingRef = useRef(false); // Track if user is actively editing (adding/removing items)

  // Expose openAddAccessDialog for external triggers (e.g. Manage button in BuildTab)
  useEffect(() => {
    if (openAddAccessRef) {
      openAddAccessRef.current = () => setShowAddAccessDialog(true);
      return () => { openAddAccessRef.current = null; };
    }
  }, [openAddAccessRef]);

  // Entity type labels
  const entityLabels = {
    prompt: {
      private: 'Private Capability',
      privateDesc: 'Only I can access this capability',
      shareCompany: 'Share with my company',
      shareCompanyDesc: 'All users in your company can view and use this capability',
      shareTeam: 'Share with my team',
      shareTeamDesc: 'All users in your team can view and use this capability',
      shareGroupsDesc: 'Select specific groups you belong to for sharing access',
      settingsTitle: 'Permission Settings',
      settingsDesc: 'Configure who can access and use this capability.'
    },
    context: {
      private: 'Private Knowledge',
      privateDesc: 'Only you can see and use this knowledge',
      shareCompany: 'Share with my company',
      shareCompanyDesc: 'All users in your company can view and use this knowledge',
      shareTeam: 'Share with my team',
      shareTeamDesc: 'All users in your team can view and use this knowledge',
      shareGroupsDesc: 'Select specific groups you belong to for sharing access',
      settingsTitle: 'Permission Settings',
      settingsDesc: 'Configure who can access and use this knowledge.'
    },
    collection: {
      private: 'Private Project',
      privateDesc: 'Only you can see and manage this project',
      shareCompany: 'Share with my company',
      shareCompanyDesc: 'All users in your company can view and add capabilities to this project',
      shareTeam: 'Share with my team',
      shareTeamDesc: 'All users in your team can view and add capabilities to this project',
      shareGroupsDesc: 'Select specific groups you belong to for sharing access',
      settingsTitle: 'Permission Settings',
      settingsDesc: 'Configure who can access and interact with this project.'
    }
  };

  const labels = entityLabels[entityType] || entityLabels.item;

  // Function to build permissions object from current state
  const buildPermissions = (
    items = accessList,
    customMode = useCustomPermissions,
    preserveSavedCustomPermissions = true
  ) => {
    // Start with a clean slate - don't use existing permissions to avoid duplicates
    let perms = {
      companyAccess: false,
      teamAccess: [],
      groupAccess: [],
      individualAccess: [],
      canEdit: [],
      canShare: [],
      canDelete: [],
      viewOnlyAccess: [] // Track users/teams/groups explicitly set to "view-only"
    };

    // If truly private (not sharing with anything), return empty permissions
    if (isPrivate && !shareWithCompany && !shareWithTeam && !useCustomPermissions) {
      return {
        companyAccess: false,
        teamAccess: [],
        groupAccess: [],
        individualAccess: [],
        canEdit: [],
        canShare: [],
        canDelete: [],
        viewOnlyAccess: []
      };
    }

    if (customMode) {
      // A saved custom grant may be resolving into display records when this
      // component first opens. Keep the saved permission shape during that
      // brief empty-list state instead of overwriting it with Private.
      if (items.length === 0 && preserveSavedCustomPermissions) {
        const savedPermissions = permissions || {};
        return {
          companyAccess: false,
          teamAccess: Array.isArray(savedPermissions.teamAccess) ? [...savedPermissions.teamAccess] : [],
          groupAccess: Array.isArray(savedPermissions.groupAccess) ? [...savedPermissions.groupAccess] : [],
          individualAccess: Array.isArray(savedPermissions.individualAccess) ? [...savedPermissions.individualAccess] : [],
          canEdit: Array.isArray(savedPermissions.canEdit) ? [...savedPermissions.canEdit] : [],
          canShare: Array.isArray(savedPermissions.canShare) ? [...savedPermissions.canShare] : [],
          canDelete: Array.isArray(savedPermissions.canDelete) ? [...savedPermissions.canDelete] : [],
          viewOnlyAccess: Array.isArray(savedPermissions.viewOnlyAccess) ? [...savedPermissions.viewOnlyAccess] : [],
        };
      }
      // Custom permissions mode - ONLY use accessList, completely ignore standard permissions
      // All arrays are already empty from initialization above
      
      // Check if accessList only contains standard team sharing (user's team with interact level)
      const userTeamId = userProfile?.teamId;
      const teamAccesses = items.filter(a => a.type === 'team');
      const nonTeamAccesses = items.filter(a => a.type !== 'team');
      
      // If only team access exists, and it's the user's team with "interact" level, convert to standard sharing
      const isOnlyStandardTeamShare = nonTeamAccesses.length === 0 && 
        teamAccesses.length === 1 && 
        teamAccesses[0].id === userTeamId && 
        teamAccesses[0].role === 'interact';
      
      if (isOnlyStandardTeamShare) {
        // Convert to standard team sharing - don't process as custom permissions
        // This will be handled by the standard permissions path below
        perms.teamAccess = [userTeamId];
      } else {
        // Process custom permissions normally
        items.forEach(access => {
        const entityId = access.id;
        if (!entityId) return;

        // Collections and contexts use simplified permissions: only 'interact' and 'owner' (co-owner)
        // Prompts use: 'view-only', 'interact', 'edit', 'owner'
        const isCollection = entityType === 'collection';
        const isContext = entityType === 'context';
        
        if (access.type === 'user') {
          perms.individualAccess.push(entityId);
          if (isCollection || isContext) {
            // Collections and contexts: only 'interact' (default) or 'owner' (co-owner)
            if (access.role === 'owner') {
              perms.canDelete.push(entityId);
            }
            // 'interact' role doesn't need special arrays - just being in individualAccess is enough
          } else {
            // Prompts: support 'view-only', 'interact', 'edit', 'owner'
            if (access.role === 'view-only') {
              perms.viewOnlyAccess.push(entityId);
            } else {
              // Everyone with access can share (no canShare restriction needed)
              if (access.role === 'edit' || access.role === 'owner') {
                perms.canEdit.push(entityId);
              }
              if (access.role === 'owner') {
                perms.canDelete.push(entityId);
              }
            }
          }
        } else if (access.type === 'team') {
          perms.teamAccess.push(entityId);
          if (isCollection || isContext) {
            // Collections and contexts: only 'interact' (default) or 'owner' (co-owner)
            if (access.role === 'owner') {
              perms.canDelete.push(entityId);
            }
          } else {
            // Prompts: support 'view-only', 'interact', 'edit', 'owner'
            if (access.role === 'view-only') {
              perms.viewOnlyAccess.push(entityId);
            } else {
              // Everyone with access can share (no canShare restriction needed)
              if (access.role === 'edit' || access.role === 'owner') {
                perms.canEdit.push(entityId);
              }
              if (access.role === 'owner') {
                perms.canDelete.push(entityId);
              }
            }
          }
        } else if (access.type === 'group') {
          perms.groupAccess.push(entityId);
          if (isCollection || isContext) {
            // Collections and contexts: only 'interact' (default) or 'owner' (co-owner)
            if (access.role === 'owner') {
              perms.canDelete.push(entityId);
            }
          } else {
            // Prompts: support 'view-only', 'interact', 'edit', 'owner'
            if (access.role === 'view-only') {
              perms.viewOnlyAccess.push(entityId);
            } else {
              // Everyone with access can share (no canShare restriction needed)
              if (access.role === 'edit' || access.role === 'owner') {
                perms.canEdit.push(entityId);
              }
              if (access.role === 'owner') {
                perms.canDelete.push(entityId);
              }
            }
          }
        }
      });
      }
    } else {
      // Standard permission toggles - explicitly set based on UI state
      // Default permission level is "interact" (can view, comment, clone, share)
      // No canEdit/canDelete arrays are populated, so users get "interact" level by default
      perms.companyAccess = shareWithCompany || false;
      
      // Team sharing is implied when company sharing is enabled
      // Also include team if explicitly enabled (for backwards compatibility)
      if ((shareWithCompany || shareWithTeam) && userProfile?.teamId) {
        perms.teamAccess = [userProfile.teamId];
      } else {
        perms.teamAccess = [];
      }
      
      // Groups are now handled via custom permissions only
      perms.groupAccess = [];
    }

    return perms;
  };

  // Initialize permission state from permissions prop (for both new and existing items)
  useEffect(() => {
    // The parent visibility controls custom mode for every entity type.
    // Do not let the normal initialization path turn a custom selection into private.
    if (visibility === 'custom') {
      setUseCustomPermissions(true);
      setIsPrivate(false);
      setShareWithCompany(false);
      setShareWithTeam(false);
      return;
    }
    // For new items, ALWAYS default to company sharing unless explicitly set to false
    // This matches prompts behavior where company sharing is the default
    if (!isEditing && !hasInitialized.current) {
      // New item - set defaults immediately
      // IMPORTANT: isPrivate and shareWithCompany are mutually exclusive
      if (permissions && permissions.companyAccess === false) {
        // Explicitly set to false - check if truly private (no other access)
        const hasOtherAccess = (permissions.teamAccess && permissions.teamAccess.length > 0) ||
                               (permissions.groupAccess && permissions.groupAccess.length > 0) ||
                               (permissions.individualAccess && permissions.individualAccess.length > 0);
        setIsPrivate(!hasOtherAccess);
        setShareWithCompany(false);
      } else {
        // Default to company sharing (not private) - they are mutually exclusive
        setIsPrivate(false);
        setShareWithCompany(true);
        // Team is implied when company is on - don't set the toggle
        setShareWithTeam(false);
      }
      setUseCustomPermissions(false);
      hasInitialized.current = true;
      return; // Exit early for new items
    }
    
    // For existing items, sync from permissions prop
    // Check if permissions has actual values (not just empty {} or undefined companyAccess)
    const hasValidPermissions = permissions && (isEditing ? true : permissions.companyAccess !== undefined);
    
    if (hasValidPermissions) {
      // Check if this permissions change came from us (by comparing with what we last sent)
      const permsString = JSON.stringify(permissions);
      const lastPerms = lastPermissionsRef.current;
      const lastPermsString = lastPerms?.permsString;
      
      // Only re-initialize if:
      // 1. We haven't initialized yet, OR
      // 2. The permissions changed AND it's different from what we last sent (external change)
      const isOurUpdate = lastPerms && 
        permissions.companyAccess === lastPerms.companyAccess &&
        JSON.stringify(permissions.teamAccess || []) === JSON.stringify(lastPerms.teamAccess || []) &&
        JSON.stringify(permissions.groupAccess || []) === JSON.stringify(lastPerms.groupAccess || []) &&
        JSON.stringify(permissions.individualAccess || []) === JSON.stringify(lastPerms.individualAccess || []) &&
        JSON.stringify(permissions.canDelete || []) === JSON.stringify(lastPerms.canDelete || []) &&
        JSON.stringify(permissions.canEdit || []) === JSON.stringify(lastPerms.canEdit || []) &&
        JSON.stringify(permissions.viewOnlyAccess || []) === JSON.stringify(lastPerms.viewOnlyAccess || []);
      
      // Only reset initialization flag if permissions changed from an external source
      // BUT don't re-initialize if user is actively editing and has items in accessList
      // (this prevents clearing accessList when user adds their team via custom permissions)
      // ALSO don't re-initialize if we're in custom permissions mode and accessList is being actively edited
      const hasActiveAccessList = accessList && accessList.length > 0;
      const isActivelyEditingCustomPermissions = useCustomPermissions && hasActiveAccessList;
      const isUserActivelyEditing = isUserEditingRef.current;
      
      if (lastPermsString !== permsString && !isOurUpdate && !hasActiveAccessList && !isActivelyEditingCustomPermissions && !isUserActivelyEditing) {
        hasInitialized.current = false;
      }
      
      // If user is actively editing custom permissions, don't re-initialize at all
      if (isActivelyEditingCustomPermissions || isUserActivelyEditing) {
        return;
      }
      
      // Only initialize if we haven't initialized yet, OR if it's an external change (not from user toggles)
      if (!hasInitialized.current || (!isOurUpdate && !isEditing)) {
        isInitializing.current = true;
        hasInitialized.current = true;
        const perms = permissions;
      
      // EXACTLY like prompts line 309-310 - simple and direct
      // IMPORTANT: isPrivate and shareWithCompany are mutually exclusive
      const isPrivateState = !perms.companyAccess && perms.teamAccess?.length === 0 && perms.groupAccess?.length === 0 && perms.individualAccess?.length === 0;
      const companyAccessState = perms.companyAccess || false;
      
      // Ensure they're mutually exclusive
      if (companyAccessState) {
        setIsPrivate(false);
        setShareWithCompany(true);
        // Team is implied when company is on - don't set the toggle
        setShareWithTeam(false);
      } else if (isPrivateState) {
        setIsPrivate(true);
        setShareWithCompany(false);
      } else {
        // Neither private nor company - default to company for new items
        if (!isEditing) {
          setIsPrivate(false);
          setShareWithCompany(true);
          setShareWithTeam(false); // Team implied when company is on
        } else {
          setIsPrivate(isPrivateState);
          setShareWithCompany(companyAccessState);
        }
      }
      
      // Declare permission arrays once for reuse (team and group checks)
      const viewOnlyAccess = Array.isArray(perms.viewOnlyAccess) ? perms.viewOnlyAccess : [];
      const canEdit = Array.isArray(perms.canEdit) ? perms.canEdit : [];
      const canDelete = Array.isArray(perms.canDelete) ? perms.canDelete : [];
      
      const userTeamId = userProfile?.teamId;
      const hasTeamAccess = perms.teamAccess && perms.teamAccess.length > 0;
      
      // Check if the user's team has any custom roles
      const teamHasCustomRoles = userTeamId && (
        canEdit.includes(userTeamId) ||
        canDelete.includes(userTeamId) ||
        viewOnlyAccess.includes(userTeamId)
      );
      
      // Standard team share = user's team, single team, no custom roles (just interact level)
      const isStandardTeamShare = hasTeamAccess && 
                                userTeamId && 
                                perms.teamAccess.includes(userTeamId) && 
                                perms.teamAccess.length === 1 &&
                                !teamHasCustomRoles;
      
      const groupIds = perms.groupAccess || [];
      
      // Check if groups have custom roles (non-standard group access)
      const hasGroupRoles = groupIds.some(groupId => 
        canEdit.includes(groupId) ||
        canDelete.includes(groupId) ||
        viewOnlyAccess.includes(groupId)
      );
      
      const userGroupId = userProfile?.groupId;
      const userGroupIds = availableGroups.length > 0 
        ? availableGroups.map(g => g.id) 
        : (userGroupId ? [userGroupId] : []);
      const groupsMatchUserGroups = groupIds.length > 0 && 
        groupIds.every(groupId => userGroupIds.includes(groupId));
      const isStandardGroupSharePattern = groupIds.length > 0 && 
        !hasGroupRoles && 
        groupsMatchUserGroups;
      
      // Custom permissions are for:
      // 1. Individual user access (specific users)
      // 2. Non-standard team access (teams that aren't the user's team, or multiple teams)
      // 3. Non-standard group access (groups with roles OR groups that don't match standard pattern)
      // Note: Company access is NOT custom permissions - it's a standard toggle
      const hasIndividualAccess = perms.individualAccess && perms.individualAccess.length > 0;
      const hasNonStandardTeamAccess = hasTeamAccess && (!isStandardTeamShare || perms.teamAccess.length > 1);
      const hasNonStandardGroupAccess = hasGroupRoles || (groupIds.length > 0 && !isStandardGroupSharePattern);
      
      const hasCustomPermissions = hasIndividualAccess || hasNonStandardTeamAccess || hasNonStandardGroupAccess || false;
      
      // Set custom permissions mode based ONLY on permissions object, not accessList
      setUseCustomPermissions(hasCustomPermissions);
      
      setSelectedGroupIds(groupIds);
      
      // IMPORTANT: Team toggle logic - must run AFTER company/private state is set
      // Only set team toggle if company is not enabled (team is implied when company is on)
      if (companyAccessState) {
        // Company is enabled - team is implied, toggle should be off
        setShareWithTeam(false);
      } else if (!perms.companyAccess) {
        // Company not enabled - check if team sharing exists
        setShareWithTeam(isStandardTeamShare);
      } else {
        // Fallback
        setShareWithTeam(false);
      }
      
      // If custom permissions are active, clear team toggle (but preserve company - it's already set above)
      if (hasCustomPermissions) {
        setShareWithTeam(false);
        setSelectedGroupIds([]);
      }
      
        setTimeout(() => {
          isInitializing.current = false;
        }, 100);
        
        // Store the permissions string to detect changes
        lastPermissionsRef.current = {
          companyAccess: perms.companyAccess,
          teamAccess: [...(perms.teamAccess || [])],
          groupAccess: [...(perms.groupAccess || [])],
          individualAccess: [...(perms.individualAccess || [])],
          canEdit: [...(perms.canEdit || [])],
          canShare: [...(perms.canShare || [])],
          canDelete: [...(perms.canDelete || [])],
          viewOnlyAccess: [...(perms.viewOnlyAccess || [])],
          permsString: JSON.stringify(permissions)
        };
      }
    } else if (!hasValidPermissions) {
      // No valid permissions provided - use defaults for new items
      if (!isEditing && !hasInitialized.current) {
        hasInitialized.current = true;
        // Reset to default sharing state (company & team) for new items
        setIsPrivate(false);
        setShareWithCompany(true);
        setShareWithTeam(true);
        setUseCustomPermissions(false);
        if (onAccessListChange) {
          onAccessListChange([]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, visibility, showCommunityOption]);

  // When BuildTab visibility toggle changes, force discrete state (prompts only)
  // Run this with higher precedence - visibility is source of truth for the BuildTab toggles
  useEffect(() => {
    if (controlledPermissionsSync) return;
    if (!showCommunityOption || visibility == null) return;
    if (visibility === 'custom') {
      setUseCustomPermissions(true);
      setIsPrivate(false);
      setShareWithCompany(false);
      setShareWithTeam(false);
      hasInitialized.current = false;
      lastPermissionsRef.current = null; // Force permissions sync to treat next permissions as external
    } else if (visibility === 'community') {
      setUseCustomPermissions(false);
      setIsPrivate(false);
      setShareWithCompany(false);
      setShareWithTeam(false);
    } else if (visibility === 'private') {
      setUseCustomPermissions(false);
      setIsPrivate(true);
      setShareWithCompany(false);
      setShareWithTeam(false);
    } else if (visibility === 'team' || visibility === 'organization') {
      setUseCustomPermissions(false);
      setIsPrivate(false);
      // Permissions are authoritative for org vs team: org preset uses companyAccess true
      // AND teamAccess [userTeamId]. Inference/localStorage can mis-label visibility as "team"
      // while companyAccess is still true — do not force team toggles in that case or we
      // overwrite org permissions on the next buildPermissions() sync.
      const p = permissions || {};
      if (p.companyAccess) {
        setShareWithCompany(true);
        setShareWithTeam(false);
      } else {
        const userTeamId = userProfile?.teamId;
        const ta = Array.isArray(p.teamAccess) ? p.teamAccess : [];
        const viewOnly = Array.isArray(p.viewOnlyAccess) ? p.viewOnlyAccess : [];
        const canEdit = Array.isArray(p.canEdit) ? p.canEdit : [];
        const canDelete = Array.isArray(p.canDelete) ? p.canDelete : [];
        const teamHasCustomRoles =
          userTeamId &&
          (canEdit.includes(userTeamId) || canDelete.includes(userTeamId) || viewOnly.includes(userTeamId));
        const singleTeam = ta.length === 1 && userTeamId && ta[0] === userTeamId;
        const looksLikeStandardTeamOnly =
          singleTeam &&
          !teamHasCustomRoles &&
          !(p.groupAccess && p.groupAccess.length) &&
          !(p.individualAccess && p.individualAccess.length);

        if (looksLikeStandardTeamOnly) {
          setShareWithCompany(false);
          setShareWithTeam(true);
        } else {
          setShareWithCompany(visibility === 'organization');
          setShareWithTeam(visibility === 'team');
        }
      }
      hasInitialized.current = false; // Re-sync from permissions
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- permissions/userProfile disambiguate visibility vs stored perms
  }, [visibility, showCommunityOption, permissions, userProfile?.teamId]);

  // Clear permission state when community prompt is enabled
  useEffect(() => {
    if (showCommunityOption && isCommunityPrompt) {
      setIsPrivate(false);
      setShareWithCompany(false);
      setShareWithTeam(false);
      setUseCustomPermissions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommunityPrompt, showCommunityOption]);

  // Initialize permissions on mount for new items - build from default state
  // This ensures permissions are set even if permissions prop is not provided initially
  useEffect(() => {
    if (controlledPermissionsSync) return;
    if (!isEditing && !hasInitialized.current && !permissions) {
      const initialPermissions = buildPermissions();
      onPermissionsChange(initialPermissions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure useCustomPermissions is true when accessList has items
  // Note: We don't auto-convert during editing - let users add their team and then other permissions
  // The conversion to standard sharing only happens when loading saved permissions
  useEffect(() => {
    if (controlledPermissionsSync) return;
    if (accessList.length > 0 && !useCustomPermissions) {
      setUseCustomPermissions(true);
    }
  }, [accessList.length, useCustomPermissions, controlledPermissionsSync]);

  // Update permissions when state changes
  useEffect(() => {
    if (controlledPermissionsSync) return;
    if (isInitializing.current) return;

    // Selecting Custom is a two-render transition: the parent exposes the
    // new visibility first, then this component enables custom mode. Do not
    // emit the previous private preset during that brief gap.
    if (visibility === 'custom' && !useCustomPermissions) return;
    
    // If community prompt is enabled, don't update permissions (community handles its own permissions)
    if (showCommunityOption && isCommunityPrompt) {
      return;
    }
    
    // Check if accessList only contains standard team sharing - if so, convert to standard sharing mode
    // This allows users to add their team first, then add others, without auto-converting
    // But when saving, if it's only standard team sharing, it will be saved as standard
    if (useCustomPermissions && accessList.length > 0) {
      const userTeamId = userProfile?.teamId;
      const teamAccesses = accessList.filter(a => a.type === 'team');
      const nonTeamAccesses = accessList.filter(a => a.type !== 'team');
      
      // eslint-disable-next-line no-unused-vars
      const isOnlyStandardTeamShare = nonTeamAccesses.length === 0 && 
        teamAccesses.length === 1 && 
        teamAccesses[0].id === userTeamId && 
        teamAccesses[0].role === 'interact';
      
      // Don't auto-convert during editing - let buildPermissions handle the conversion when saving
      // This allows users to add their team, then add others, without losing custom permissions mode
    }
    
    const newPermissions = buildPermissions();
    
    // Compare with last permissions we sent to avoid circular updates
    const lastPerms = lastPermissionsRef.current;
    const hasChanged = !lastPerms || 
      newPermissions.companyAccess !== lastPerms.companyAccess ||
      JSON.stringify(newPermissions.teamAccess || []) !== JSON.stringify(lastPerms.teamAccess || []) ||
      JSON.stringify(newPermissions.groupAccess || []) !== JSON.stringify(lastPerms.groupAccess || []) ||
      JSON.stringify(newPermissions.individualAccess || []) !== JSON.stringify(lastPerms.individualAccess || []) ||
      JSON.stringify(newPermissions.canEdit || []) !== JSON.stringify(lastPerms.canEdit || []) ||
      JSON.stringify(newPermissions.canShare || []) !== JSON.stringify(lastPerms.canShare || []) ||
      JSON.stringify(newPermissions.canDelete || []) !== JSON.stringify(lastPerms.canDelete || []) ||
      JSON.stringify(newPermissions.viewOnlyAccess || []) !== JSON.stringify(lastPerms.viewOnlyAccess || []);
    
    if (!hasChanged) {
      return; // Skip update if nothing changed
    }
    
    // Store the permissions we're about to send BEFORE calling onPermissionsChange
    // This allows us to detect our own updates in the initialization useEffect
    lastPermissionsRef.current = {
      companyAccess: newPermissions.companyAccess,
      teamAccess: [...(newPermissions.teamAccess || [])],
      groupAccess: [...(newPermissions.groupAccess || [])],
      individualAccess: [...(newPermissions.individualAccess || [])],
      canEdit: [...(newPermissions.canEdit || [])],
      canShare: [...(newPermissions.canShare || [])],
      canDelete: [...(newPermissions.canDelete || [])],
      viewOnlyAccess: [...(newPermissions.viewOnlyAccess || [])],
      permsString: JSON.stringify(newPermissions)
    };
    
    onPermissionsChange(newPermissions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate, shareWithCompany, shareWithTeam, useCustomPermissions, accessList, isCommunityPrompt, availableGroups, controlledPermissionsSync]);

  // Prompt builder: parent owns preset permissions; only sync custom accessList edits upward
  useEffect(() => {
    if (!controlledPermissionsSync) return;
    if (isInitializing.current) return;
    if (showCommunityOption && isCommunityPrompt) return;
    if (!useCustomPermissions) return;
    // BuildTab SSOT: non-custom presets live on promptData; never overwrite from stale accessList
    if (visibility != null && visibility !== 'custom') return;

    // Keep saved grants intact while the custom picker resolves the display
    // records. An explicit empty save already updates `permissions`, so it is
    // still represented as an empty custom grant set on the following render.
    const newPermissions = buildPermissions();

    const lastPerms = lastPermissionsRef.current;
    const hasChanged =
      !lastPerms ||
      newPermissions.companyAccess !== lastPerms.companyAccess ||
      JSON.stringify(newPermissions.teamAccess || []) !== JSON.stringify(lastPerms.teamAccess || []) ||
      JSON.stringify(newPermissions.groupAccess || []) !== JSON.stringify(lastPerms.groupAccess || []) ||
      JSON.stringify(newPermissions.individualAccess || []) !== JSON.stringify(lastPerms.individualAccess || []) ||
      JSON.stringify(newPermissions.canEdit || []) !== JSON.stringify(lastPerms.canEdit || []) ||
      JSON.stringify(newPermissions.canShare || []) !== JSON.stringify(lastPerms.canShare || []) ||
      JSON.stringify(newPermissions.canDelete || []) !== JSON.stringify(lastPerms.canDelete || []) ||
      JSON.stringify(newPermissions.viewOnlyAccess || []) !== JSON.stringify(lastPerms.viewOnlyAccess || []);

    if (!hasChanged) return;

    lastPermissionsRef.current = {
      companyAccess: newPermissions.companyAccess,
      teamAccess: [...(newPermissions.teamAccess || [])],
      groupAccess: [...(newPermissions.groupAccess || [])],
      individualAccess: [...(newPermissions.individualAccess || [])],
      canEdit: [...(newPermissions.canEdit || [])],
      canShare: [...(newPermissions.canShare || [])],
      canDelete: [...(newPermissions.canDelete || [])],
      viewOnlyAccess: [...(newPermissions.viewOnlyAccess || [])],
      permsString: JSON.stringify(newPermissions)
    };

    onPermissionsChange(newPermissions);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow deps for prompt SSOT
  }, [controlledPermissionsSync, accessList, useCustomPermissions, isCommunityPrompt, showCommunityOption, visibility]);

  // Load all company groups for the custom-access picker (no userId filter — matches backend listGroups).
  const loadAvailableGroups = async () => {
    if (!userProfile?.companyId || !user?.id) return;
    // Allow reloading if we have selectedGroupIds but they're not in availableGroups
    if (availableGroups.length > 0) {
      // Check if all selected groups are in availableGroups
      const missingGroups = selectedGroupIds.filter(id => !availableGroups.find(g => g.id === id));
      if (missingGroups.length === 0) return; // All selected groups are already loaded
    }

    setLoadingGroups(true);
    try {
      const response = await listGroups(userProfile.companyId);
      const allGroups = response.groups || [];
      const formattedGroups = allGroups.map((group) => ({
        id: group.id,
        name: group.name,
        description: group.description || ''
      }));

      setAvailableGroups(formattedGroups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Load groups when component mounts or when permissions change (needed for standard group detection)
  useEffect(() => {
    if (userProfile?.companyId && user?.id) {
      // Load groups if we don't have them yet, or if permissions have groups we need to check
      const groupIds = permissions?.groupAccess || [];
      const needsGroups = groupIds.length > 0 || selectedGroupIds.length > 0;
      if (needsGroups && availableGroups.length === 0) {
        loadAvailableGroups();
      } else if (groupIds.length > 0) {
        // Check if we have all groups from permissions
        const missingGroups = groupIds.filter(id => !availableGroups.find(g => g.id === id));
        if (missingGroups.length > 0) {
          loadAvailableGroups();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions?.groupAccess, selectedGroupIds.length, userProfile?.companyId, user?.id]);

  // Cascading permission logic handlers
  const handlePrivateToggle = (checked) => {
    setIsPrivate(checked);
    if (checked) {
      setShareWithCompany(false);
      setShareWithTeam(false);
      setUseCustomPermissions(false);
    }
  };

  const handleCompanyToggle = (checked) => {
    setShareWithCompany(checked);
    if (checked) {
      setIsPrivate(false);
      // Team sharing is implied when company sharing is enabled (no need to toggle)
    }
  };

  const handleTeamToggle = (checked) => {
    setShareWithTeam(checked);
    if (checked) {
      setIsPrivate(false);
    }
  };


  const handleCustomPermissionsToggle = (checked) => {
    setUseCustomPermissions(checked);
    if (checked) {
      setIsPrivate(false);
      setShareWithCompany(false);
      setShareWithTeam(false);
    }
  };

  return (
    <>
      <Stack spacing={3}>
        {/* Alert if user cannot edit permissions */}
        {isEditing && !canEditPermissions && (
          <Alert severity="info">
            You can edit the {entityType} content, but only the creator or users with owner permission can change permissions.
          </Alert>
        )}
        
        {/* When hideLegacyToggles (prompt builder): streamlined UI uses BuildTab visibility toggles + Manage */}
        {hideLegacyToggles && (
          <Alert severity="info">
            Use the Permissions controls above to manage who can access this capability.
          </Alert>
        )}
        
        {/* Permission toggles - only show if not community prompt and not hideLegacyToggles */}
        {!hideLegacyToggles && (!showCommunityOption || !isCommunityPrompt) && (
          <>
            {/* Private Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={isPrivate}
                  onChange={(e) => handlePrivateToggle(e.target.checked)}
                  color="primary"
                  disabled={!canEditPermissions || useCustomPermissions || isCommunityPrompt}
                  sx={{ opacity: isEditing && !canEditPermissions ? 0.5 : 1 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {labels.private}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {labels.privateDesc}
                  </Typography>
                </Box>
              }
            />

            {/* Share with Company */}
            <FormControlLabel
              control={
                <Switch
                  checked={shareWithCompany}
                  onChange={(e) => handleCompanyToggle(e.target.checked)}
                  disabled={!canEditPermissions || isPrivate || useCustomPermissions || isCommunityPrompt}
                  color="primary"
                  sx={{ opacity: isEditing && !canEditPermissions ? 0.5 : 1 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {labels.shareCompany}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {labels.shareCompanyDesc}
                  </Typography>
                </Box>
              }
            />

            {/* Share with Team */}
            <FormControlLabel
              control={
                <Switch
                  checked={shareWithTeam}
                  onChange={(e) => handleTeamToggle(e.target.checked)}
                  disabled={!canEditPermissions || isPrivate || useCustomPermissions || isCommunityPrompt}
                  color="primary"
                  sx={{ opacity: isEditing && !canEditPermissions ? 0.5 : 1 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {labels.shareTeam}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {labels.shareTeamDesc}
                  </Typography>
                </Box>
              }
            />

            {/* Custom Permissions Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomPermissions}
                  onChange={(e) => handleCustomPermissionsToggle(e.target.checked)}
                  disabled={!canEditPermissions || isPrivate || isCommunityPrompt}
                  color="primary"
                  sx={{ opacity: isEditing && !canEditPermissions ? 0.5 : 1 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Custom Permissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set specific permissions for individual users, teams, or groups
                  </Typography>
                </Box>
              }
            />

            {useCustomPermissions && (
              <>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Custom permissions override all other sharing settings. You can manage specific access below.
                </Alert>

                {/* Custom Permissions Section - shows below the toggle */}
                <Paper sx={{ p: 3, mt: 2, opacity: isEditing && !canEditPermissions ? 0.5 : 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Manage Custom Permissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Grant specific access to users, teams, or groups with custom permission levels.
                  </Typography>
                  
                  <Stack spacing={2}>
                    {accessList
                      .filter(access => {
                        // Filter out the creator - they should not appear in custom permissions list
                        if (access.type === 'user' && creatorId && access.id === creatorId) {
                          return false;
                        }
                        return true;
                      })
                      .map((access) => (
                      <Card key={access.id} variant="outlined">
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {access.type === 'user' && (
                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                                  {getUserInitials(access)}
                                </Avatar>
                              )}
                              {access.type === 'team' && <GroupsIcon fontSize="small" />}
                              {access.type === 'group' && <GroupIcon fontSize="small" />}
                              <Box>
                                <Typography variant="subtitle2" fontWeight="medium">
                                  {access.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {access.type.charAt(0).toUpperCase() + access.type.slice(1)}
                                  {access.description && ` • ${access.description}`}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={permissionLevels?.[access.role]?.label || access.role} 
                                size="small"
                                variant="outlined"
                                onClick={canEditPermissions ? (e) => {
                                  setPermissionMenuAnchor(e.currentTarget);
                                  setEditingAccessId(access.id);
                                } : undefined}
                                sx={{ cursor: canEditPermissions ? 'pointer' : 'default' }}
                                disabled={!canEditPermissions}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  isUserEditingRef.current = true;
                                  onAccessListChange(accessList.filter(a => a.id !== access.id));
                                  setTimeout(() => { isUserEditingRef.current = false; }, 1000);
                                }}
                                color="error"
                                disabled={!canEditPermissions}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                    
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setShowAddAccessDialog(true)}
                      fullWidth
                      disabled={!canEditPermissions}
                    >
                      Add Custom Access
                    </Button>
                  </Stack>
                </Paper>
              </>
            )}
          </>
        )}

        {/* Community Prompt Option (only for prompts) - hide when prompt builder uses streamlined UI */}
        {!hideLegacyToggles && showCommunityOption && (
          <>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  disabled={isEditing && !canEditPermissions}
                  sx={{ opacity: isEditing && !canEditPermissions ? 0.5 : 1 }}
                  checked={isCommunityPrompt}
                  onChange={(e) => onCommunityToggle && onCommunityToggle(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Make this a community capability
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Share with the entire PromptCrew community - automatically public, cloneable, and comments enabled
                  </Typography>
                </Box>
              }
            />
            {isCommunityPrompt && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Community capabilities are automatically public, cloneable, and have comments enabled.
                They will be visible to all users in the community.
              </Alert>
            )}
          </>
        )}
      </Stack>

      {/* Custom Permissions Dialog */}
      <CustomPermissionsDialog
        open={showAddAccessDialog}
        onClose={() => setShowAddAccessDialog(false)}
        onSave={(nextAccessList) => {
          isUserEditingRef.current = true;
          setUseCustomPermissions(true);
          onAccessListChange(nextAccessList);

          const newPermissions = buildPermissions(nextAccessList, true, false);
          lastPermissionsRef.current = {
            companyAccess: newPermissions.companyAccess,
            teamAccess: [...(newPermissions.teamAccess || [])],
            groupAccess: [...(newPermissions.groupAccess || [])],
            individualAccess: [...(newPermissions.individualAccess || [])],
            canEdit: [...(newPermissions.canEdit || [])],
            canShare: [...(newPermissions.canShare || [])],
            canDelete: [...(newPermissions.canDelete || [])],
            viewOnlyAccess: [...(newPermissions.viewOnlyAccess || [])],
            permsString: JSON.stringify(newPermissions)
          };
          onPermissionsChange(newPermissions);
          setTimeout(() => { isUserEditingRef.current = false; }, 1000);
        }}
        accessList={accessList}
        permissionLevels={permissionLevels}
        title="Add Custom Access"
        creatorId={creatorId}
        canEdit={canEditPermissions}
      />

      {/* Permission Level Menu */}
      <Menu
        anchorEl={permissionMenuAnchor}
        open={Boolean(permissionMenuAnchor)}
        onClose={() => {
          setPermissionMenuAnchor(null);
          setEditingAccessId(null);
        }}
      >
        {permissionLevels && Object.entries(permissionLevels).map(([key, level]) => {
          const currentAccess = accessList.find(a => a.id === editingAccessId);
          const isSelected = currentAccess?.role === key;
          return (
            <MenuItem
              key={key}
              selected={isSelected}
              onClick={() => {
                const updatedAccessList = accessList.map(access => 
                  access.id === editingAccessId ? { ...access, role: key } : access
                );
                onAccessListChange(updatedAccessList);
                setPermissionMenuAnchor(null);
                setEditingAccessId(null);
              }}
            >
              <ListItemIcon>{level.icon}</ListItemIcon>
              <ListItemText primary={level.label} secondary={level.description} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default PermissionSettings;

